import { useState, type ReactNode } from 'react';
import {
  ACCESS_BLOCK_LABELS,
  ACCESS_BLOCKS,
  BACKUP_RETENTION_DAYS,
  ENVIRONMENTS,
  ENVIRONMENT_LABELS,
  emptyCapabilityGrants,
  type AllowlistEntryDto,
  type CapabilityGrants,
  type ConnectionDto,
  type ConnectionProbeResult,
  type Environment,
  type ToolkitUserDto,
} from '@visualitics-toolkit/shared';

import { Badge, Button, Checkbox, EmptyState, Field, Input, Select } from '../components/atoms';
import { ErrorBlock, LoadingBlock, StatusBlock, formatDateTime } from '../components/molecules';
import { Dialog } from '../components/organisms/Dialog';
import {
  useActivateConnection,
  useActiveConnection,
  useAllowlist,
  useConnections,
  useCreateToolkitUser,
  useDeleteConnection,
  useProbeConnection,
  useResetToolkitUserPassword,
  useSaveAllowlist,
  useSaveConnection,
  useSession,
  useTestConnection,
  useToolkitUsers,
  useUpdateToolkitUser,
} from '../lib/queries';

const TABS = ['conexoes', 'allowlist', 'usuarios', 'retencao', 'seguranca'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  conexoes: 'Conexões',
  allowlist: 'Allowlist',
  usuarios: 'Usuários',
  retencao: 'Retenção',
  seguranca: 'Segurança',
};

export function SettingsPage(): ReactNode {
  const session = useSession();
  const isAdmin = Boolean(session.data?.isAdmin);
  const [tab, setTab] = useState<Tab>('conexoes');
  const visibleTabs = TABS.filter((value) => value !== 'usuarios' || isAdmin);

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-content">Configurações</h1>

      <div role="tablist" className="flex gap-1 border-b border-border">
        {visibleTabs.map((value) => (
          <button
            key={value}
            role="tab"
            type="button"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
            className={
              tab === value
                ? 'border-b-2 border-accent px-3 py-2 text-sm font-medium text-accent'
                : 'px-3 py-2 text-sm text-muted hover:text-content'
            }
          >
            {TAB_LABELS[value]}
          </button>
        ))}
      </div>

      {tab === 'conexoes' && <ConnectionsTab isAdmin={isAdmin} />}
      {tab === 'allowlist' && <AllowlistTab isAdmin={isAdmin} />}
      {tab === 'usuarios' && isAdmin && <UsersTab />}
      {tab === 'retencao' && <RetentionTab />}
      {tab === 'seguranca' && <SecurityTab />}
    </div>
  );
}

function ConnectionsTab({ isAdmin }: { isAdmin: boolean }): ReactNode {
  const connections = useConnections();
  const session = useSession();
  const test = useTestConnection();
  const activate = useActivateConnection();
  const remove = useDeleteConnection();
  const [editing, setEditing] = useState<ConnectionDto | 'new' | null>(null);

  return (
    <section className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => setEditing('new')}>
            Nova conexão
          </Button>
        </div>
      )}
      {!isAdmin && (
        <p className="text-sm text-muted">Somente administradores podem criar ou editar conexões.</p>
      )}

      {connections.isLoading && <LoadingBlock />}
      {connections.data?.length === 0 && (
        <EmptyState
          title="Nenhuma conexão"
          description="Cadastre o Postgres do Visualitics (local, staging ou produção)."
        />
      )}

      <ul className="space-y-2">
        {connections.data?.map((connection) => (
          <li key={connection.id} className="card flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-sm font-medium text-content">
                {connection.name}
                <Badge tone={connection.environment === 'production' ? 'danger' : 'accent'}>
                  {ENVIRONMENT_LABELS[connection.environment]}
                </Badge>
                {connection.isDefault && <Badge>padrão</Badge>}
              </p>
              <p className="font-mono text-xs text-muted">
                {connection.username}@{connection.host}:{connection.port}/{connection.database} ·
                sslmode={connection.sslmode}
              </p>
              <p className="text-xs text-muted">
                último teste: {formatDateTime(connection.lastTestedAt)}
                {connection.lastTestOk === true && ' (ok)'}
                {connection.lastTestOk === false && ' (falhou)'} · servidor:{' '}
                {connection.serverVersion ?? '—'}
              </p>
              {test.isSuccess && test.variables === connection.id && test.data && (
                <StatusBlock className="mt-2" message={formatProbeMessage(test.data)} />
              )}
            </div>
            <div className="flex gap-1">
              <Button
                loading={test.isPending && test.variables === connection.id}
                onClick={() => test.mutate(connection.id)}
              >
                Testar
              </Button>
              <Button
                variant={connection.id === session.data?.activeConnectionId ? 'ghost' : 'primary'}
                disabled={connection.id === session.data?.activeConnectionId}
                loading={activate.isPending && activate.variables === connection.id}
                onClick={() => activate.mutate(connection.id)}
              >
                {connection.id === session.data?.activeConnectionId ? 'em uso' : 'Ativar'}
              </Button>
              {isAdmin && (
                <>
                  <Button onClick={() => setEditing(connection)}>Editar</Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Remover a conexão "${connection.name}"?`)) remove.mutate(connection.id);
                    }}
                  >
                    Remover
                  </Button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>

      {test.error instanceof Error && <ErrorBlock message={test.error.message} />}
      {activate.error instanceof Error && <ErrorBlock message={activate.error.message} />}

      {editing && isAdmin && (
        <ConnectionDialog
          connection={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function ConnectionDialog({
  connection,
  onClose,
}: {
  connection: ConnectionDto | null;
  onClose: () => void;
}): ReactNode {
  const save = useSaveConnection();
  const probe = useProbeConnection();
  const [values, setValues] = useState({
    name: connection?.name ?? '',
    environment: (connection?.environment ?? 'local') as Environment,
    host: connection?.host ?? 'localhost',
    port: connection?.port ?? 5432,
    database: connection?.database ?? 'visualitics',
    username: connection?.username ?? '',
    password: '',
    sslmode: connection?.sslmode ?? 'disable',
    sshTunnelNote: connection?.sshTunnelNote ?? '',
    isDefault: connection?.isDefault ?? false,
    visualiticsApiUrl: connection?.visualiticsApiUrl ?? '',
    visualiticsEmail: connection?.visualiticsEmail ?? '',
    visualiticsPassword: '',
  });

  function set<K extends keyof typeof values>(key: K, value: (typeof values)[K]): void {
    probe.reset();
    setValues((current) => ({ ...current, [key]: value }));
  }

  return (
    <Dialog title={connection ? 'Editar conexão' : 'Nova conexão'} onClose={onClose} wide>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Field label="Nome *" htmlFor="conn-name">
          <Input id="conn-name" value={values.name} onChange={(event) => set('name', event.target.value)} />
        </Field>
        <Field label="Ambiente *" htmlFor="conn-environment" help="Define a cor do shell e a allowlist padrão.">
          <Select
            id="conn-environment"
            value={values.environment}
            onChange={(event) => set('environment', event.target.value as Environment)}
          >
            {ENVIRONMENTS.map((environment) => (
              <option key={environment} value={environment}>
                {ENVIRONMENT_LABELS[environment]}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Host *"
          htmlFor="conn-host"
          help="Do notebook: 127.0.0.1 (a API no Docker reescreve para host.docker.internal). Não use o IP 172.x da VM."
        >
          <Input id="conn-host" value={values.host} onChange={(event) => set('host', event.target.value)} />
        </Field>
        <Field label="Porta *" htmlFor="conn-port">
          <Input
            id="conn-port"
            type="number"
            value={values.port}
            onChange={(event) => set('port', Number(event.target.value))}
          />
        </Field>
        <Field label="Database *" htmlFor="conn-database">
          <Input id="conn-database" value={values.database} onChange={(event) => set('database', event.target.value)} />
        </Field>
        <Field label="Usuário *" htmlFor="conn-username" help="Em produção, use o papel dedicado de menor privilégio.">
          <Input id="conn-username" value={values.username} onChange={(event) => set('username', event.target.value)} />
        </Field>
        <Field
          label={connection ? 'Senha (deixe vazio para manter)' : 'Senha *'}
          htmlFor="conn-password"
          help="Guardada com AES-256-GCM; nunca é devolvida pela API."
        >
          <Input
            id="conn-password"
            type="password"
            autoComplete="new-password"
            value={values.password}
            onChange={(event) => set('password', event.target.value)}
          />
        </Field>
        <Field label="sslmode">
          <Select value={values.sslmode} onChange={(event) => set('sslmode', event.target.value)}>
            {['disable', 'require', 'verify-ca', 'verify-full'].map((mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Field label="Nota do túnel SSH" help="Só documentação — o toolkit não abre túnel sozinho.">
        <Input
          value={values.sshTunnelNote}
          onChange={(event) => set('sshTunnelNote', event.target.value)}
        />
      </Field>

      <div className="mt-2 border-t border-border pt-4">
        <p className="mb-3 text-sm font-medium text-content">API Visualitics (ZIP de CSV)</p>
        <p className="mb-3 text-xs text-muted">
          Usada só em Ingerir ZIP de CSV. A senha é cifrada no profile e nunca volta na API nem em
          ops_runs. O ZIP de CSV não vai para o bucket público.
        </p>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <Field
            label="URL da API"
            htmlFor="conn-visualitics-url"
            help="Ex.: https://dashboard.visualitics.ai/api — local: http://host.docker.internal:3000/api"
          >
            <Input
              id="conn-visualitics-url"
              value={values.visualiticsApiUrl}
              onChange={(event) => set('visualiticsApiUrl', event.target.value)}
            />
          </Field>
          <Field label="E-mail Visualitics" htmlFor="conn-visualitics-email">
            <Input
              id="conn-visualitics-email"
              type="email"
              value={values.visualiticsEmail}
              onChange={(event) => set('visualiticsEmail', event.target.value)}
            />
          </Field>
          <Field
            label={
              connection?.hasVisualiticsPassword
                ? 'Senha Visualitics (deixe vazio para manter)'
                : 'Senha Visualitics'
            }
            htmlFor="conn-visualitics-password"
            help="SUPER_ADMIN ou ADMIN da org. AES-256-GCM; nunca é devolvida."
          >
            <Input
              id="conn-visualitics-password"
              type="password"
              autoComplete="new-password"
              value={values.visualiticsPassword}
              onChange={(event) => set('visualiticsPassword', event.target.value)}
            />
          </Field>
        </div>
      </div>

      <Checkbox
        label="Usar como conexão padrão"
        checked={values.isDefault}
        onChange={(event) => set('isDefault', event.target.checked)}
      />

      {save.error instanceof Error && <ErrorBlock className="mt-3" message={save.error.message} />}
      {probe.error instanceof Error && <ErrorBlock className="mt-3" message={probe.error.message} />}
      {probe.isSuccess && probe.data && (
        <StatusBlock className="mt-3" message={formatProbeMessage(probe.data)} />
      )}

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          loading={probe.isPending}
          disabled={!values.host || !values.username || (!values.password && !connection)}
          onClick={() =>
            probe.mutate({
              host: values.host,
              port: values.port,
              database: values.database,
              username: values.username,
              password: values.password || undefined,
              sslmode: values.sslmode,
              connectionId: connection?.id,
            })
          }
        >
          Testar estes dados
        </Button>
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={!values.name || !values.username || (!connection && !values.password)}
          onClick={() =>
            save.mutate(
              {
                id: connection?.id,
                values: {
                  ...values,
                  password: values.password || undefined,
                  sshTunnelNote: values.sshTunnelNote || undefined,
                  visualiticsApiUrl: values.visualiticsApiUrl,
                  visualiticsEmail: values.visualiticsEmail,
                  visualiticsPassword: values.visualiticsPassword || undefined,
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          Salvar
        </Button>
      </div>
    </Dialog>
  );
}

function AllowlistTab({ isAdmin }: { isAdmin: boolean }): ReactNode {
  const connection = useActiveConnection();
  const allowlist = useAllowlist(connection?.id);
  const save = useSaveAllowlist(connection?.id ?? '');

  if (!connection) return <EmptyState title="Ative uma conexão para editar a allowlist" />;
  if (allowlist.isLoading) return <LoadingBlock />;

  return (
    <section className="space-y-3">
      <p className="text-sm text-muted">
        Override por conexão sobre o default do registry. A avaliação é sempre no servidor —
        desmarcar aqui não é a única barreira.
        {!isAdmin && ' Somente administradores podem alterar overrides.'}
      </p>

      {save.error instanceof Error && <ErrorBlock message={save.error.message} />}

      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-surface-raised text-xs uppercase text-muted">
            <tr>
              <th className="px-3 py-2">Operação</th>
              {ENVIRONMENTS.map((environment) => (
                <th key={environment} className="px-3 py-2 text-center">
                  {ENVIRONMENT_LABELS[environment]}
                </th>
              ))}
              <th className="px-3 py-2">
                Override em {ENVIRONMENT_LABELS[connection.environment]}
              </th>
            </tr>
          </thead>
          <tbody>
            {allowlist.data?.map((entry) => (
              <AllowlistRow
                key={entry.operationId}
                entry={entry}
                environment={connection.environment}
                readOnly={!isAdmin}
                onChange={(allowed) => save.mutate([{ operationId: entry.operationId, allowed }])}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AllowlistRow({
  entry,
  environment,
  readOnly,
  onChange,
}: {
  entry: AllowlistEntryDto;
  environment: Environment;
  readOnly: boolean;
  onChange: (allowed: boolean | null) => void;
}): ReactNode {
  const value = entry.overrideAllowed === null ? 'default' : entry.overrideAllowed ? 'allow' : 'deny';

  return (
    <tr className="border-t border-border/60">
      <td className="px-3 py-1.5">
        {entry.title}
        <span className="ml-2 font-mono text-[11px] text-muted">{entry.operationId}</span>
      </td>
      {ENVIRONMENTS.map((env) => (
        <td key={env} className="px-3 py-1.5 text-center">
          {entry.defaultAllowed[env] ? (
            <span className="text-severity-reversible">permitida</span>
          ) : (
            <span className="text-muted">bloqueada</span>
          )}
        </td>
      ))}
      <td className="px-3 py-1.5">
        <Select
          value={value}
          disabled={readOnly}
          onChange={(event) =>
            onChange(event.target.value === 'default' ? null : event.target.value === 'allow')
          }
        >
          <option value="default">
            usar padrão ({entry.defaultAllowed[environment] ? 'permitida' : 'bloqueada'})
          </option>
          <option value="allow">forçar permitida</option>
          <option value="deny">forçar bloqueada</option>
        </Select>
      </td>
    </tr>
  );
}

function UsersTab(): ReactNode {
  const users = useToolkitUsers(true);
  const [editing, setEditing] = useState<ToolkitUserDto | 'new' | null>(null);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted">
          Matriz por operador: quais blocos (Consulta / Edição / Remoções) em quais ambientes.
        </p>
        <Button variant="primary" onClick={() => setEditing('new')}>
          Novo usuário
        </Button>
      </div>

      {users.isLoading && <LoadingBlock />}
      {users.error instanceof Error && <ErrorBlock message={users.error.message} />}

      <ul className="space-y-2">
        {users.data?.map((user) => (
          <li key={user.id} className="card flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-sm font-medium text-content">
                {user.name}
                {user.isAdmin && <Badge tone="accent">admin</Badge>}
              </p>
              <p className="font-mono text-xs text-muted">{user.email}</p>
              <div className="mt-2 space-y-1 text-xs text-muted">
                {ACCESS_BLOCKS.map((block) => (
                  <p key={block}>
                    <span className="text-content">{ACCESS_BLOCK_LABELS[block]}:</span>{' '}
                    {user.grants[block].length
                      ? user.grants[block].map((env) => ENVIRONMENT_LABELS[env]).join(', ')
                      : '—'}
                  </p>
                ))}
              </div>
            </div>
            <Button onClick={() => setEditing(user)}>Editar</Button>
          </li>
        ))}
      </ul>

      {editing && (
        <UserDialog user={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

function UserDialog({
  user,
  onClose,
}: {
  user: ToolkitUserDto | null;
  onClose: () => void;
}): ReactNode {
  const create = useCreateToolkitUser();
  const update = useUpdateToolkitUser();
  const resetPassword = useResetToolkitUserPassword();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(user?.isAdmin ?? false);
  const [grants, setGrants] = useState<CapabilityGrants>(user?.grants ?? emptyCapabilityGrants());

  function toggleGrant(block: (typeof ACCESS_BLOCKS)[number], environment: Environment): void {
    setGrants((current) => {
      const list = current[block];
      const next = list.includes(environment)
        ? list.filter((item) => item !== environment)
        : [...list, environment];
      return { ...current, [block]: next };
    });
  }

  const pending = create.isPending || update.isPending || resetPassword.isPending;
  const error =
    (create.error instanceof Error && create.error.message) ||
    (update.error instanceof Error && update.error.message) ||
    (resetPassword.error instanceof Error && resetPassword.error.message) ||
    null;

  return (
    <Dialog title={user ? 'Editar usuário' : 'Novo usuário'} onClose={onClose} wide>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Field label="Nome *" htmlFor="user-name">
          <Input id="user-name" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="E-mail *" htmlFor="user-email">
          <Input
            id="user-email"
            type="email"
            value={email}
            disabled={Boolean(user)}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field
          label={user ? 'Nova senha (opcional)' : 'Senha *'}
          htmlFor="user-password"
          help="Mínimo 8 caracteres."
        >
          <Input
            id="user-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        <div className="flex items-end pb-3">
          <Checkbox
            label="Administrador (gestão de usuários, conexões e allowlist)"
            checked={isAdmin}
            onChange={(event) => setIsAdmin(event.target.checked)}
          />
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-md border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-surface-raised text-xs uppercase text-muted">
            <tr>
              <th className="px-3 py-2">Bloco</th>
              {ENVIRONMENTS.map((environment) => (
                <th key={environment} className="px-3 py-2 text-center">
                  {ENVIRONMENT_LABELS[environment]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ACCESS_BLOCKS.map((block) => (
              <tr key={block} className="border-t border-border/60">
                <td className="px-3 py-2 font-medium text-content">{ACCESS_BLOCK_LABELS[block]}</td>
                {ENVIRONMENTS.map((environment) => (
                  <td key={environment} className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      aria-label={`${ACCESS_BLOCK_LABELS[block]} em ${ENVIRONMENT_LABELS[environment]}`}
                      checked={grants[block].includes(environment)}
                      onChange={() => toggleGrant(block, environment)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && <ErrorBlock className="mt-3" message={error} />}

      <div className="mt-4 flex justify-end gap-2">
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          loading={pending}
          disabled={!name || (!user && (!email || password.length < 8))}
          onClick={() => {
            if (!user) {
              create.mutate(
                { email, name, password, isAdmin, grants },
                { onSuccess: onClose },
              );
              return;
            }
            update.mutate(
              { id: user.id, body: { name, isAdmin, grants } },
              {
                onSuccess: () => {
                  if (password.length >= 8) {
                    resetPassword.mutate({ id: user.id, password }, { onSuccess: onClose });
                  } else {
                    onClose();
                  }
                },
              },
            );
          }}
        >
          Salvar
        </Button>
      </div>
    </Dialog>
  );
}

function RetentionTab(): ReactNode {
  return (
    <section className="card space-y-2 text-sm">
      <h2 className="font-medium text-content">Retenção de backups por ambiente</h2>
      <ul className="space-y-1 text-muted">
        {ENVIRONMENTS.map((environment) => (
          <li key={environment}>
            {ENVIRONMENT_LABELS[environment]}:{' '}
            <strong className="text-content">{BACKUP_RETENTION_DAYS[environment]} dias</strong>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted">
        A limpeza roda ao final de cada backup bem-sucedido, removendo arquivo e registro.
      </p>
    </section>
  );
}

function formatProbeMessage(result: ConnectionProbeResult): string {
  return `Conexão ok via ${result.resolvedHost} — PostgreSQL ${result.serverVersion} em ${result.latencyMs} ms`;
}

function SecurityTab(): ReactNode {
  return (
    <section className="card space-y-2 text-sm text-muted">
      <h2 className="font-medium text-content">Segurança</h2>
      <ul className="list-inside list-disc space-y-1">
        <li>Senhas de conexão são cifradas com AES-256-GCM e nunca retornam pela API.</li>
        <li>A chave de criptografia vive apenas em variável de ambiente do servidor.</li>
        <li>
          Permissões por operador (Consulta / Edição / Remoções × ambiente) e allowlist por conexão
          são avaliadas no servidor a cada execução.
        </li>
        <li>Toda tentativa — inclusive as recusadas — vira um registro imutável em runs.</li>
        <li>Restore pela UI é restrito a local/staging; produção segue runbook manual.</li>
      </ul>
    </section>
  );
}
